import { useDareRequirementMessages, } from 'root/src/client/logic/dropEvent/hooks/useDareRequirementMessages';
import { memo, useMemo } from 'react';
import { useUserCreatorData } from 'root/src/client/logic/auth/hooks/useUser';
import { useDropEvent, useUserCampaignData } from 'root/src/client/logic/dropEvent/hooks/useDropEvent';
import { useViewershipPlatformsByDares } from 'root/src/client/logic/dropEvent/hooks/useViewershipPlatformsByDares';
import InfoIcon from '@mui/icons-material/Info';
import * as Popover from '@radix-ui/react-popover';
import IconButton from '@mui/material/IconButton';
import { DarePlatformIcon, PlatformIconTooltip } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { calculateMaxDaresByPlatform } from 'root/src/shared/util/calculateMaxDares';
import NumberFlow from '@number-flow/react';
import { BaseTooltip, BaseTooltipContent } from 'root/src/client/v2/common/components/BaseTooltip/BaseTooltip';
import { useMaxDaresTotal } from 'root/src/client/logic/dropEvent/hooks/useMaxDaresTotal';
export const DaresLeft = memo(() => {
    const { daresLeft, daresLeftPerPlatform, messages, isVisible } = useDaresLeft();
    if (!isVisible) {
        return null;
    }
    return (<div className="flex bg-secondary items-center gap-0.5 font-bold tracking-wide shadow drop-shadow flex-initial self-center pl-1 py-0.5 pr-6 rounded-2xl">
			<Popover.Root>
				<BaseTooltip tooltip={(<BaseTooltipContent className="font-bold">Click for more info</BaseTooltipContent>)}>
					<Popover.Trigger asChild>
						<IconButton>
							<InfoIcon />
						</IconButton>
					</Popover.Trigger>
				</BaseTooltip>
				<Popover.Portal>
					<Popover.Content onOpenAutoFocus={event => event.preventDefault()} sideOffset={5} className="p-2 animate-pop-up bg-secondary drop-shadow-2xl shadow-2xl rounded-xl">
						<div className="w-fit md:w-[500px] border-primary border divide-primary bg-secondary shadow text-xs gap-4 flex flex-col p-4 rounded-xl z-50">
							<ul className="grid grid-cols-1 sm:grid-cols-2 md:flex gap-6 md:divide-x divide-primary">
								{daresLeftPerPlatform.map(({ platform, left }) => {
            return (<li className="flex" key={platform}>
											<PlatformMaxDaresInfo platform={platform} maxDares={left}/>
										</li>);
        })}
							</ul>
							<hr className="border-primary"/>
							<RequirementMessages messages={messages}/>
						</div>
					</Popover.Content>
				</Popover.Portal>
			</Popover.Root>
			<p>Dares left: <NumberFlow value={daresLeft}/></p>
		</div>);
});
const PlatformMaxDaresInfo = memo(({ platform, maxDares }) => {
    return (<div className="flex flex-col gap-2 items-center justify-center">
			<PlatformIconTooltip platform={platform}>
				<DarePlatformIcon platform={platform} size="20"/>
			</PlatformIconTooltip>
			<p className="text-xl font-bold">
				<NumberFlow value={maxDares}/>
			</p>
			<p>Dares left</p>
		</div>);
});
const RequirementMessages = memo(({ messages }) => {
    return (<ul className="flex flex-col gap-2">
			{messages.map(message => (<li key={message} className="list-disc list-inside">
					{message}
				</li>))}
		</ul>);
});
const useDaresLeft = () => {
    const { averageCcv = 0, tiktokFollowers = 0, averageYoutubeViews = 0, averageYoutubeShortsViews = 0 } = useUserCreatorData();
    const dropEvent = useDropEvent();
    const userCampaignData = useUserCampaignData(dropEvent === null || dropEvent === void 0 ? void 0 : dropEvent.mnemonicId);
    const { viewershipPlatformsByDares } = useViewershipPlatformsByDares();
    const maxDaresTotal = useMaxDaresTotal();
    const messages = useDareRequirementMessages();
    const isVisible = viewershipPlatformsByDares.length > 0;
    const daresLeft = useMemo(() => {
        return Math.max(0, maxDaresTotal - ((userCampaignData === null || userCampaignData === void 0 ? void 0 : userCampaignData.creatorAcceptedDares) || 0));
    }, [maxDaresTotal, userCampaignData === null || userCampaignData === void 0 ? void 0 : userCampaignData.creatorAcceptedDares]);
    const daresLeftPerPlatform = useMemo(() => {
        if (!dropEvent) {
            return [];
        }
        return viewershipPlatformsByDares.map((platform) => {
            var _a;
            const maxDares = calculateMaxDaresByPlatform({
                averageCcv,
                averageYoutubeViews,
                averageYoutubeShortsViews,
                tiktokFollowers,
                platform,
                campaign: dropEvent,
            });
            return {
                platform,
                left: Math.max(0, maxDares - (((_a = userCampaignData === null || userCampaignData === void 0 ? void 0 : userCampaignData.creatorAcceptedDaresByPlatform) === null || _a === void 0 ? void 0 : _a[platform]) || 0)),
            };
        });
    }, [viewershipPlatformsByDares, dropEvent, averageCcv, averageYoutubeViews, tiktokFollowers, userCampaignData]);
    return { daresLeft, messages, daresLeftPerPlatform, isVisible };
};
